import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  Eye, 
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Package,
  AlertTriangle,
  RefreshCcw,
  Loader2
} from 'lucide-react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn, flattenStateHistory } from '../../lib/utils';
import { OrderStateHistory } from '../../components/admin/OrderStateHistory';
import { RefundComposer } from '../../components/admin/RefundComposer';
import { ForceTransitionPanel, TransitionOption } from '../../components/admin/ForceTransitionPanel';

interface Order {
  id: string;
  customer: {
    name: string;
    email: string;
    id: string;
  };
  business: {
    name: string;
    id: string;
  };
  driver?: {
    name: string;
    id: string;
  };
  items: { id: string; name: string; price: number; quantity: number }[];
  total: number;
  deliveryFee: number;
  state: string;
  createdAt: Date;
  updatedAt: Date;
  stateHistory: any;
  flags?: Record<string, any>;
}

function toDate(value: any): Date {
  if (!value) return new Date(0);
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

/** Map a raw order doc into the admin table shape. */
function mapOrderDoc(id: string, data: any): Order {
  return {
    id,
    customer: {
      name: data.customerName || 'Customer',
      email: data.customerEmail || '',
      id: data.shopperId || '',
    },
    business: {
      name: data.businessName || data.businessId || 'Boutique',
      id: data.businessId || '',
    },
    driver: data.driverId
      ? { name: data.driver?.name || data.uber?.courier?.name || 'Driver', id: data.driverId }
      : data.uber?.courier?.name
        ? { name: data.uber.courier.name, id: 'uber' }
        : undefined,
    items: (data.items || []).map((item: any, idx: number) => ({
      id: String(idx),
      name: item.name || 'Item',
      price: item.price || 0,
      quantity: item.quantity || 1,
    })),
    total: data.total || 0,
    deliveryFee: data.deliveryFee || 0,
    state: data.state || 'CREATED',
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    stateHistory: data.stateHistory || {},
    flags: data.flags,
  };
}

/** Convert the order's stateHistory map into the timeline component's shape. */
function mapStateHistory(stateHistory: any) {
  return Object.entries(flattenStateHistory(stateHistory))
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([key, entry]: [string, any]) => ({
      id: key,
      fromState: entry.fromState || '',
      toState: entry.toState || '',
      action: entry.action || '',
      actor: {
        id: entry.actorId || 'unknown',
        type: (entry.actorRole || 'system') as 'shopper' | 'business' | 'driver' | 'admin' | 'system',
      },
      timestamp: toDate(entry.timestamp),
      metadata: entry.metadata,
    }))
    .reverse();
}

const stateColors: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
  PRICED: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock },
  PAYMENT_AUTHORIZED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
  PENDING_BUSINESS_APPROVAL: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  BUSINESS_APPROVED: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: CheckCircle },
  DISPATCHING_DRIVER: { bg: 'bg-purple-100', text: 'text-purple-700', icon: RefreshCcw },
  DRIVER_ASSIGNED: { bg: 'bg-cyan-100', text: 'text-cyan-700', icon: Truck },
  PICKED_UP: { bg: 'bg-violet-100', text: 'text-violet-700', icon: Package },
  DELIVERED: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  COMPLETED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  DISPUTED: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-500', icon: XCircle },
  REFUNDED: { bg: 'bg-orange-100', text: 'text-orange-700', icon: RefreshCcw },
};

interface OrderDetailPanelProps {
  order: Order;
  onClose: () => void;
}

function OrderDetailPanel({ order, onClose }: OrderDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'refund' | 'actions'>('details');
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const stateStyle = stateColors[order.state] || stateColors.PRICED;
  const StateIcon = stateStyle.icon;

  const history = useMemo(() => mapStateHistory(order.stateHistory), [order.stateHistory]);

  // Admin override → transitionOrder callable (audited server-side)
  const handleForceTransition = async (option: TransitionOption, reason: string) => {
    setFeedback(null);
    const transition = httpsCallable(functions, 'transitionOrder');
    await transition({
      orderId: order.id,
      action: option.action,
      actorRole: 'admin',
      clientRequestId: `admin_${order.id}_${option.action}_${option.targetState || ''}_${Date.now()}`,
      metadata: {
        ...(option.targetState ? { targetState: option.targetState } : {}),
        reason,
      },
    });
    setFeedback({ kind: 'success', message: `Order moved ${option.targetState ? `to ${option.targetState}` : 'forward'}.` });
  };

  // Refund composer → adminRefundOrder callable
  const handleRefund = async (refundData: {
    itemRefunds: { itemId: string; amount: number }[];
    deliveryFeeRefund: number;
    reason: string;
  }) => {
    setFeedback(null);
    const itemTotal = refundData.itemRefunds.reduce((sum, r) => sum + r.amount, 0);
    const refundAmount = itemTotal + refundData.deliveryFeeRefund;
    const isFull = Math.abs(refundAmount - order.total) < 0.01;

    try {
      const refund = httpsCallable(functions, 'adminRefundOrder');
      await refund({
        orderId: order.id,
        ...(isFull ? {} : { refundAmount }),
        itemIds: refundData.itemRefunds.map((r) => r.itemId),
        refundDeliveryFee: refundData.deliveryFeeRefund > 0,
        reason: refundData.reason,
      });
      setFeedback({ kind: 'success', message: `Refund of $${refundAmount.toFixed(2)} processed.` });
    } catch (err: any) {
      setFeedback({ kind: 'error', message: err?.message || 'Refund failed.' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-serif text-heading">Order #{order.id.slice(-6)}</h2>
              <p className="text-xs sm:text-sm text-body truncate">Created {new Date(order.createdAt).toLocaleString()}</p>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <span className={cn("px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium flex items-center space-x-1", stateStyle.bg, stateStyle.text)}>
                <StateIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">{order.state.replace(/_/g, ' ')}</span>
              </span>
              <button onClick={onClose} className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <XCircle className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs - Scrollable on mobile */}
        <div className="px-4 sm:px-6 border-b border-gray-100 overflow-x-auto">
          <div className="flex space-x-4 sm:space-x-6 min-w-max">
            {[
              { id: 'details', label: 'Details' },
              { id: 'history', label: 'History' },
              { id: 'refund', label: 'Refund' },
              { id: 'actions', label: 'Actions' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-body hover:text-heading"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {feedback && (
            <div
              className={cn(
                'mb-4 p-3 rounded-xl text-sm flex items-start gap-2',
                feedback.kind === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-700'
              )}
            >
              {feedback.kind === 'success' ? (
                <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              )}
              {feedback.message}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-4 sm:space-y-6">
              {/* Customer & Business */}
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                    <CardTitle className="text-sm sm:text-base">Customer</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <p className="font-medium text-heading text-sm sm:text-base">{order.customer.name}</p>
                    <p className="text-xs sm:text-sm text-body truncate">{order.customer.email}</p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-1 font-mono">ID: {order.customer.id}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                    <CardTitle className="text-sm sm:text-base">Business</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <p className="font-medium text-heading text-sm sm:text-base">{order.business.name}</p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-1 font-mono">ID: {order.business.id}</p>
                    {order.driver && (
                      <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
                        <p className="text-xs sm:text-sm text-body">Driver: {order.driver.name}</p>
                        <p className="text-[10px] sm:text-xs text-gray-400 font-mono">ID: {order.driver.id}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Items */}
              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                  <CardTitle className="text-sm sm:text-base">Items</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="space-y-2 sm:space-y-3">
                    {order.items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-heading text-sm sm:text-base truncate">{item.name}</p>
                          <p className="text-xs sm:text-sm text-body">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-semibold text-heading text-sm sm:text-base flex-shrink-0">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 space-y-1.5 sm:space-y-2">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-body">Delivery Fee</span>
                      <span className="text-heading">${order.deliveryFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span className="text-heading text-sm sm:text-base">Total</span>
                      <span className="text-heading text-base sm:text-lg">${order.total.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'history' && (
            <OrderStateHistory orderId={order.id} history={history} />
          )}

          {activeTab === 'refund' && (
            <RefundComposer
              orderId={order.id}
              items={order.items}
              deliveryFee={order.deliveryFee}
              totalPaid={order.total}
              onSubmit={handleRefund}
              onCancel={onClose}
            />
          )}

          {activeTab === 'actions' && (
            <ForceTransitionPanel
              orderId={order.id}
              currentState={order.state}
              onTransition={handleForceTransition}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminOrders() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  // Live subscription to the most recent orders
  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setOrders(snap.docs.map((d) => mapOrderDoc(d.id, d.data())));
        setLoading(false);
      },
      (err) => {
        console.error('[AdminOrders] subscription error:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // Detail panel stays in sync with live order updates
  const selectedOrder = useMemo(
    () => orders.find((o) => o.id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );
  const setSelectedOrder = (order: Order | null) => setSelectedOrderId(order?.id || null);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.business.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.state === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const statusOptions = ['all', ...Object.keys(stateColors)];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Orders</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">Manage and monitor all platform orders</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs sm:text-sm text-body">{filteredOrders.length} orders</span>
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <Input
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 sm:pl-10 text-sm h-9 sm:h-10"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 h-9 sm:h-10 text-sm"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={cn("h-4 w-4 transition-transform", showFilters && "rotate-180")} />
            </Button>
          </div>

          {showFilters && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {statusOptions.slice(0, 6).map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={cn(
                      "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors",
                      statusFilter === status
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-body hover:bg-gray-200"
                    )}
                  >
                    {status === 'all' ? 'All' : status.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Orders - Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {filteredOrders.map((order) => {
          const stateStyle = stateColors[order.state] || stateColors.PRICED;
          const StateIcon = stateStyle.icon;

          return (
            <Card 
              key={order.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedOrder(order)}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center space-x-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-medium text-xs flex-shrink-0">
                      {order.customer.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-heading text-sm truncate">{order.customer.name}</p>
                      <p className="text-xs text-body font-mono">#{order.id.slice(-6)}</p>
                    </div>
                  </div>
                  <span className={cn(
                    "inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0",
                    stateStyle.bg, stateStyle.text
                  )}>
                    <StateIcon className="h-2.5 w-2.5" />
                    <span className="truncate max-w-[80px]">{order.state.replace(/_/g, ' ')}</span>
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-xs text-body mb-2">
                  <span className="truncate">{order.business.name}</span>
                  {order.driver && (
                    <span className="flex items-center ml-2">
                      <Truck className="h-3 w-3 mr-1" />
                      <span className="truncate max-w-[80px]">{order.driver.name}</span>
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div>
                    <p className="font-semibold text-heading text-sm">${order.total.toFixed(2)}</p>
                    <p className="text-[10px] text-body">{order.items.length} item(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-heading">{new Date(order.createdAt).toLocaleDateString()}</p>
                    <p className="text-[10px] text-body">{new Date(order.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Orders - Desktop Table View */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Business</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOrders.map((order) => {
                  const stateStyle = stateColors[order.state] || stateColors.PRICED;
                  const StateIcon = stateStyle.icon;

                  return (
                    <tr 
                      key={order.id} 
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedOrder(order)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-heading">#{order.id.slice(-6)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-heading text-sm">{order.customer.name}</p>
                        <p className="text-xs text-body truncate max-w-[150px]">{order.customer.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-heading text-sm">{order.business.name}</p>
                        {order.driver && (
                          <p className="text-xs text-body flex items-center">
                            <Truck className="h-3 w-3 mr-1" />
                            {order.driver.name}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-heading text-sm">${order.total.toFixed(2)}</p>
                        <p className="text-xs text-body">{order.items.length} item(s)</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          "inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium",
                          stateStyle.bg, stateStyle.text
                        )}>
                          <StateIcon className="h-3 w-3" />
                          <span>{order.state.replace(/_/g, ' ')}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-heading">{new Date(order.createdAt).toLocaleDateString()}</p>
                        <p className="text-xs text-body">{new Date(order.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrder(order);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {filteredOrders.length === 0 && (
        <Card>
          <CardContent className="py-8 sm:py-12 text-center">
            <Package className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-heading font-medium text-sm sm:text-base">No orders found</p>
            <p className="text-body text-xs sm:text-sm mt-1">Try adjusting your search or filters</p>
          </CardContent>
        </Card>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailPanel order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
    </div>
  );
}

